import { usePaymentProcessingInfo } from '@melio/ap-widgets';
import {
  Button,
  Container,
  Divider,
  Form,
  Group,
  IconButton,
  Layout,
  Link,
  Text,
  UseMelioFormResults,
} from '@melio/penny';
import {
  Bill,
  DeliveryMethod,
  FileInfo,
  FundingSource,
  Payment,
  PaymentSettingsCalculations,
  Vendor,
} from '@melio/platform-api';
import { MessageKey, useMelioIntl } from '@melio/platform-i18n';
import { useConfig } from '@melio/platform-provider';
import React, { ComponentProps } from 'react';

import { getSkipMemoToVendorStep, getSkipVendorEmail } from '../../../single-payment/utils/scheduleFlowStepsUtils';
import { PaymentFlowFormFields, PaymentFlowOnChangeHandlers } from '../../types';
import { AmountInput } from './components/AmountInput/AmountInput';
import { DeliveryDateInput } from './components/DeliveryDateInput';
import { DeliveryMethodBanner } from './components/DeliveryMethodBanner/DeliveryMethodBanner';
import { FrequencySelector } from './components/FrequencySelector';
import { FundingSourceInput } from './components/FundingSourceInput/FundingSourceInput';
import { NoteToVendorInput } from './components/NoteToVendorInput';
import { VendorEmailInput } from './components/VendorEmailInput';
import { VendorInput } from './components/VendorInput';

export enum PaymentFlowAction {
  CreatePayment,
  EditPayment,
}

type PaymentFlowFormProps = {
  form: UseMelioFormResults<PaymentFlowFormFields>;
  paymentSettings: PaymentSettingsCalculations | undefined;
  onVendorChange: PaymentFlowOnChangeHandlers['onVendorChange'];
  onAmountChange: PaymentFlowOnChangeHandlers['onAmountChange'];
  onBillButtonClick: VoidFunction;
  onFundingSourceChange: PaymentFlowOnChangeHandlers['onFundingSourceChange'];
  onNewFundingSourceClick: ComponentProps<typeof FundingSourceInput>['onNewFundingSourceClick'];
  onDeliveryDateChange: PaymentFlowOnChangeHandlers['onDeliveryDateChange'];
  onDeliveryMethodClick: VoidFunction;
  onRecurrenceChange: PaymentFlowOnChangeHandlers['onRecurrenceChange'];
  fundingSources: FundingSource[];
  deliveryMethods: DeliveryMethod[];
  vendors: Vendor[];
  payment?: Payment;
  bill?: Bill;
  file?: FileInfo;
  isLoading: boolean;
  paymentFlowAction: PaymentFlowAction;
  onClose: VoidFunction;
};

export const PaymentFlowForm = ({
  form,
  paymentSettings,
  onVendorChange,
  onAmountChange,
  onBillButtonClick,
  onFundingSourceChange,
  onNewFundingSourceClick,
  onDeliveryDateChange,
  onDeliveryMethodClick,
  onRecurrenceChange,
  fundingSources,
  deliveryMethods,
  payment,
  bill,
  isLoading,
  paymentFlowAction,
  onClose,
  vendors,
}: PaymentFlowFormProps) => {
  const { formatMessage } = useMelioIntl();
  const { formProps, submitButtonProps, watch } = form;
  const [vendorId, deliveryMethodId] = watch(['vendorId', 'deliveryMethodId']);
  const selectedVendor = vendors.find((vendor) => vendor.id === vendorId);

  const selectedDeliveryMethod = deliveryMethods.find((deliveryMethod) => deliveryMethod.id === deliveryMethodId);
  const defaultDeliveryPreference = paymentSettings?.deliveryPreferenceOptions?.[0];

  const { isPaymentProcessedByCapitalOne } = usePaymentProcessingInfo();
  const {
    settings: {
      payment: {
        memoToVendor: { deliveryMethodBlackList: memoToVendorDeliveryMethodBlackList },
      },
    },
  } = useConfig();

  const isCapitalOneRail = isPaymentProcessedByCapitalOne(selectedDeliveryMethod?.type);

  const skipNoteToVendor = getSkipMemoToVendorStep({
    deliveryMethod: selectedDeliveryMethod,
    isCapitalOneRail,
    memoToVendorDeliveryMethodBlackList,
  });

  const skipVendorEmail = getSkipVendorEmail(selectedVendor);
  const allowBillEditing = form.getValues('recurrenceType') === 'one_time';

  return (
    <Layout
      data-testid="payment-flow-form"
      variant="6Columns"
      backgroundColor="white"
      footer={{
        isSticky: true,
        content: (
          <Container data-testid="payment-flow-form-footer">
            <Divider variant="horizontal" />
            <Container justifyContent="flex-end" width="full" paddingX="s" paddingY="s">
              <Button
                {...submitButtonProps}
                data-testid="payment-flow-form-footer-submit"
                size="medium"
                label={formatMessage('activities.paymentFlow.form.footer.submit')}
              />
            </Container>
          </Container>
        ),
      }}
    >
      <Container width="full" justifyContent="center" alignItems="center" data-testid="payment-flow-form-content">
        <Group width="full" variant="vertical" spacing="xl">
          <Container width="full" data-testid="payment-flow-form-content-header">
            <Group>
              <IconButton
                data-testid="payment-flow-form-content-header-button"
                aria-label="icon-button-close"
                icon="arrow-left"
                size="large"
                onClick={onClose}
              />
              <Text textStyle="heading1Semi" data-testid="payment-flow-form-content-header-text">
                {formatMessage(getTitleMessageKey(paymentFlowAction))}
              </Text>
            </Group>
          </Container>
          <Form {...formProps}>
            <Group width="full" variant="vertical" spacing="l">
              <Group width="full" variant="vertical" spacing="xs">
                <VendorInput form={form} onChange={onVendorChange} payment={payment} bill={bill} />
                {vendorId && (
                  <DeliveryMethodBanner
                    loading={isLoading}
                    deliveryMethod={selectedDeliveryMethod}
                    onChange={onDeliveryMethodClick}
                  />
                )}
              </Group>
              <AmountInput bill={bill} form={form} onChange={onAmountChange} isReadOnly={Boolean(payment)} />
              {allowBillEditing && (
                <Link
                  data-testid="payment-flow-form-content-bill-button"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    onBillButtonClick();
                  }}
                  label={formatMessage(getBillButtonMessageKey(bill), { invoiceNumber: bill?.invoice.number })}
                />
              )}
              <FundingSourceInput
                form={form}
                onChange={onFundingSourceChange}
                onNewFundingSourceClick={onNewFundingSourceClick}
                fundingSources={fundingSources}
                isLoading={isLoading}
              />
              <FrequencySelector
                value={form.getValues('recurrenceType')}
                disabled={!!bill}
                onChange={(recurrenceType) => onRecurrenceChange({ recurrenceType })}
              />
              <DeliveryDateInput
                form={form}
                onChange={onDeliveryDateChange}
                deliveryPreference={defaultDeliveryPreference}
                isLoading={isLoading}
              />
              {skipNoteToVendor ? null : <NoteToVendorInput form={form} />}
              {skipVendorEmail ? null : <VendorEmailInput form={form} />}
            </Group>
          </Form>
        </Group>
      </Container>
    </Layout>
  );
};

const getBillButtonMessageKey = (bill: Bill | undefined): MessageKey => {
  if (!bill) {
    return 'activities.paymentFlow.form.content.bill.add.label';
  }

  return bill.invoice.number
    ? 'activities.paymentFlow.form.content.bill.edit.label.invoice'
    : 'activities.paymentFlow.form.content.bill.edit.label';
};

const getTitleMessageKey = (paymentFlowAction: PaymentFlowAction): MessageKey => {
  switch (paymentFlowAction) {
    case PaymentFlowAction.CreatePayment:
      return 'activities.paymentFlow.form.header.title.newPayment';
    case PaymentFlowAction.EditPayment:
      return 'activities.paymentFlow.form.header.title.editPayment';
  }
};
